import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Crown, Loader2, ShieldCheck, FileText, Repeat,
  Check, ShieldX, AlertTriangle, Bell, CalendarRange, RefreshCw, Mail, Users, Send,
} from "lucide-react";
import { toast } from "sonner";
import SeoHead from "../components/SeoHead";
import { useAuth } from "../lib/auth";
import { usePricing } from "../lib/usePricing";
import { createCheckoutSession, createBillingPortalSession } from "../lib/api";
import { LIVE_PRO_FEATURES } from "../lib/proFeatures";

const CHIP = {
  AVOID: { label: "AVOID", cls: "bg-risk-bg text-risk-deep border-risk-border", Icon: ShieldX },
  CAUTION: { label: "CAUTION", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  OK: { label: "No label conflict", cls: "bg-safe-bg text-safe-deep border-safe-border", Icon: Check },
};

// Real, in-app visual previews for each live feature (built from the live
// design system — not mock art).
function RoutinePreview() {
  const rows = [
    ["Retinol serum", "AVOID"],
    ["BHA exfoliant", "CAUTION"],
    ["Ceramide moisturizer", "OK"],
    ["Mineral SPF 50", "OK"],
  ];
  return (
    <div className="rounded-2xl bg-white border border-surface-line p-5 shadow-subtle w-full">
      <p className="text-[10px] uppercase tracking-[0.2em] text-ink-mute font-bold mb-3">
        Routine · with isotretinoin
      </p>
      <ul className="space-y-2">
        {rows.map(([name, v]) => {
          const c = CHIP[v];
          return (
            <li key={name} className="flex items-center justify-between gap-3 text-[13.5px] text-ink">
              <span>{name}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-bold ${c.cls}`}>
                <c.Icon className="w-3 h-3" /> {c.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReportPreview() {
  return (
    <div className="rounded-2xl bg-white border border-surface-line p-5 shadow-subtle w-full">
      <p className="text-[10px] uppercase tracking-[0.2em] text-ink-mute font-bold">BlendSafe</p>
      <p className="text-lg font-extrabold text-ink mt-1">Discussion Report</p>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2 text-[13px] text-ink">
          <FileText className="w-4 h-4 text-ink-mute" /> Findings linked to FDA / DailyMed sources
        </div>
        <div className="flex items-center gap-2 text-[13px] text-ink">
          <ShieldCheck className="w-4 h-4 text-safe-deep" /> Reviewed by a licensed US pharmacist, with date
        </div>
        <div className="flex items-center gap-2 text-[13px] text-ink">
          <Check className="w-4 h-4 text-safe-deep" /> Questions to ask your prescriber
        </div>
      </div>
    </div>
  );
}

function SwapPreview() {
  return (
    <div className="rounded-2xl bg-white border border-surface-line p-5 shadow-subtle w-full">
      <p className="text-[10px] uppercase tracking-[0.2em] text-ink-mute font-bold mb-3">
        What to use instead
      </p>
      <div className="rounded-xl border border-surface-line p-4">
        <p className="text-[14px] font-bold text-ink">Gentle ceramide moisturizer</p>
        <p className="text-[12.5px] text-ink-soft mt-1">
          Fragrance-free, checked against your medication's label.
        </p>
        <p className="text-[11px] text-ink-mute mt-2 inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5 text-safe-deep" /> Reviewed · source linked
        </p>
      </div>
    </div>
  );
}

const PREVIEW = { routine: RoutinePreview, report: ReportPreview, swaps: SwapPreview };
const ICON = {
  routine: Repeat, report: FileText, swaps: ShieldCheck,
  alerts: Bell, tracker: CalendarRange, recheck: RefreshCw,
  digest: Mail, profiles: Users, share: Send,
};

export default function Pro({ onAuthNeeded }) {
  const { user } = useAuth();
  const pricing = usePricing();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [managing, setManaging] = useState(false);
  const isPro = !!user?.is_pro;

  const startPro = async () => {
    if (!user) {
      onAuthNeeded?.();
      return;
    }
    if (isPro) {
      // Already subscribed — never start a second checkout. Send to billing.
      navigate("/account");
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckoutSession("pro_monthly");
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Checkout failed";
      toast.error("Couldn't open checkout", {
        description: typeof msg === "string" ? msg : "Please try again.",
      });
      setLoading(false);
    }
  };

  const manageSubscription = async () => {
    setManaging(true);
    try {
      const { url } = await createBillingPortalSession();
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No portal URL");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error("Couldn't open billing portal", {
        description: typeof msg === "string" ? msg : "Please try again in a moment.",
      });
      setManaging(false);
    }
  };

  return (
    <main data-testid="pro-page" className="bg-cream-50 min-h-[80vh]">
      <SeoHead
        title="BlendSafe Pro — routine checks, discussion reports, what to use instead"
        description="BlendSafe Pro checks your whole routine against your medications, builds a dermatologist discussion report with linked sources, and shows what to use instead."
        canonicalPath="/pro"
      />

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-6">
        <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
          BlendSafe Pro
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]">
          What Pro gets you
        </h1>
        <p className="text-[14.5px] sm:text-[15.5px] text-ink-soft/85 mt-5 leading-relaxed max-w-xl">
          Single checks stay free, always. Pro is for people managing a
          prescription — it checks your whole routine, builds a report for your
          prescriber, and shows what to use instead.
        </p>

        {!isPro && (
          <>
            <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span data-testid="pro-price" className="text-2xl font-extrabold text-ink tracking-[-0.02em]">
                {pricing.formatted}
              </span>
              <span className="text-ink-mute text-sm">/{pricing.interval}</span>
              <span className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full bg-cream-100 border border-surface-line text-ink-soft">
                Cancel anytime
              </span>
            </div>
            <p data-testid="pro-founding" className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium" style={{ color: "#a87a32" }}>
              <Crown className="w-3.5 h-3.5" strokeWidth={2.3} />
              Founding members: Pro free for 12 months.
            </p>
          </>
        )}

        {isPro ? (
          <div className="mt-6" data-testid="pro-already-active">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-safe-bg border border-safe-border text-safe-deep text-[13px] font-bold">
              <Crown className="w-4 h-4" strokeWidth={2.3} /> You're on BlendSafe Pro
            </div>
            <p className="mt-3 text-[14px] text-ink-soft max-w-xl">
              Your subscription is active — there's nothing more to buy. Manage or
              cancel it anytime from billing.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                data-testid="pro-manage-btn"
                onClick={manageSubscription}
                disabled={managing}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                {managing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Opening billing…</>
                ) : (
                  <>Manage subscription <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <Link
                to="/account"
                data-testid="pro-account-link"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
              >
                Go to your account
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              data-testid="pro-start-btn"
              onClick={startPro}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…</>
              ) : (
                <>Start Pro · {pricing.formatted}/{pricing.interval} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <Link
              to="/check"
              data-testid="pro-free-link"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
            >
              Try a free single check
            </Link>
          </div>
        )}
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-16 space-y-4">
        {LIVE_PRO_FEATURES.map((f, i) => {
          const Preview = PREVIEW[f.key];
          const Icon = ICON[f.key] || ShieldCheck;
          return (
            <article
              key={f.key}
              data-testid={`pro-feature-${f.key}`}
              className={`rounded-3xl bg-white border border-surface-line p-6 sm:p-8 gap-6 items-center ${Preview ? "grid md:grid-cols-2" : "block"}`}
            >
              <div className={Preview && i % 2 ? "md:order-2" : ""}>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-cream-100 border border-surface-line text-ink">
                  <Icon className="w-5 h-5" strokeWidth={1.7} />
                </span>
                <h2 className="mt-4 text-xl sm:text-2xl font-extrabold tracking-[-0.02em] text-ink">
                  {f.title}
                </h2>
                <p className="mt-2 text-[14.5px] text-ink-soft leading-relaxed">
                  {f.blurb}
                </p>
              </div>
              {Preview && (
                <div className={i % 2 ? "md:order-1" : ""}>
                  <Preview />
                </div>
              )}
            </article>
          );
        })}

        <div className="pt-4 text-center">
          {isPro ? (
            <button
              type="button"
              data-testid="pro-manage-btn-bottom"
              onClick={manageSubscription}
              disabled={managing}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {managing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Opening billing…</>
              ) : (
                <>Manage subscription <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          ) : (
            <button
              type="button"
              data-testid="pro-start-btn-bottom"
              onClick={startPro}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              Start Pro · {pricing.formatted}/{pricing.interval}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
